
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Trash2 } from "lucide-react";  

import {
useGetCourseBySlugQuery,
useAddLessonMutation,
useEditLessonMutation,
} from "@/features/api/courseApi";

import { StringField, TextareaField } from "@/components/shared/FormFields";

import { toast } from "react-hot-toast";
import DOMPurify from "dompurify";

import RichTextEditor from "@/components/ui/RichTextEditor";
import MultipleChoiceExercise from "@/assets/AllCourses/Exercises/MultipleChoice";
import FillInTheBlanksExercise from "@/assets/AllCourses/Exercises/FillInTheBlanks";
import PictureFillInTheBlanksExercise from "@/assets/AllCourses/Exercises/PictureFillInTheBlanks";
import MatchingExercise from "@/assets/AllCourses/Exercises/Matching";
import PronunciationExercise from "@/assets/AllCourses/Exercises/Pronunciation";
import ListeningExercise from "@/assets/AllCourses/Exercises/Listening";
import DragAndDropExercise from "@/assets/AllCourses/Exercises/DragAndDrop";
import CategorizingWordsExercise from "@/assets/AllCourses/Exercises/CategorizingWords";
import CircleTheCorrectWordExercise from "@/assets/AllCourses/Exercises/CircleTheCorrectWord";
import CompleteTheConversationExercise from "@/assets/AllCourses/Exercises/CompleteTheConversation";
import CompleteTheSentencesExercise from "@/assets/AllCourses/Exercises/CompleteTheSentences";
import CrosswordExercise from "@/assets/AllCourses/Exercises/Crossword";
import MissingAlphabetsExercise from "@/assets/AllCourses/Exercises/MissingAlphabets";
import SentenceJumblesExercise from "@/assets/AllCourses/Exercises/SentenceJumbles";
import StressingSyllableWordsExercise from "@/assets/AllCourses/Exercises/StressingSyllableWords";
import VocabularyBuildingExercise from "@/assets/AllCourses/Exercises/VocabularyBuilding";
import WordFormationExercise from "@/assets/AllCourses/Exercises/WordFormation";
import WordJumblesExercise from "@/assets/AllCourses/Exercises/WordJumbles";

/* -----------------------------------------
   Exercise Types
----------------------------------------- */
const exerciseTypes = [
  "exercise-section",
  "multiple-choice",
  "fill-in-the-blanks",
  "picture-fill-in-the-blanks",
  "matching",
  "pronunciation",
  "listening",
  "drag-and-drop",
  "categorizing-words",
  "circle-the-correct-word",
  "complete-the-conversation",
  "complete-the-sentences",
  "crossword",
  "missing-alphabets",
  "sentence-jumbles",
  "stressing-syllable-words",
  "vocabulary-building",
  "word-formation",
  "word-jumbles",
];

export const formatExerciseType = (type) =>
  String(type || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// const makeId = () => undefined;
const makeId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);



/* -----------------------------------------
   Reusable Fields
----------------------------------------- */

const exerciseComponents = {
  "multiple-choice": MultipleChoiceExercise,
  "fill-in-the-blanks": FillInTheBlanksExercise,
  "picture-fill-in-the-blanks": PictureFillInTheBlanksExercise,
  "matching": MatchingExercise,
  "pronunciation": PronunciationExercise,
  "listening": ListeningExercise,
  "drag-and-drop": DragAndDropExercise,
  "categorizing-words": CategorizingWordsExercise,
  "circle-the-correct-word": CircleTheCorrectWordExercise,
  "complete-the-conversation": CompleteTheConversationExercise,
  "complete-the-sentences": CompleteTheSentencesExercise,
  "crossword": CrosswordExercise,
  "missing-alphabets": MissingAlphabetsExercise,
  "sentence-jumbles": SentenceJumblesExercise,
  "stressing-syllable-words": StressingSyllableWordsExercise,
  "vocabulary-building": VocabularyBuildingExercise,
  "word-formation": WordFormationExercise,
  "word-jumbles": WordJumblesExercise,
  "exercise-section": () => null,
};

const ExerciseFields = ({ type, exercise, index, onUpdate, errors }) => {
  const Component = exerciseComponents[type];

  if (!Component) {
    return <div className="text-red-500">Unsupported exercise type</div>;
  }

  return (
    <Component
      exercise={{ ...exercise, index }}
      onUpdate={onUpdate}
      errors={errors}
    />
  );
};

const getExerciseDefaultsByType = (type) => {
  switch (type) {


      case "exercise-section":
  return {};

    case "fill-in-the-blanks":
      return {
        fillInTheBlanksQuestions: [
          {
            sentence: "",
            answers: [],
            image: "",
            requireCapitalization: false,
          },
        ],
      };

    case "picture-fill-in-the-blanks":
      return {
        pictureFillInTheBlanksQuestions: [
          { sentence: "", answers: [], image: "" },
        ],
      };

    case "multiple-choice":
  return {
    multipleChoiceQuestions: [
      {
        question: "",
        options: ["", "", "", ""],
        answer: "",
        requireCapitalization: false,
      },
    ],
  };


    case "matching":
      return {
        matchingPairs: [{ question: "", answer: "" }],
      };

    case "drag-and-drop":
      return {
        dragAndDropQuestions: [
          { sentence: "", options: [], answer: [] },
        ],
      };

    case "categorizing-words":
      return {
        words: [],
        categories: [],
      };

    case "circle-the-correct-word":
      return {
        wordChoiceQuestions: [
          { sentence: "", options: ["", ""], correct: "" },
        ],
      };

case "complete-the-conversation":
  return {
    conversationQuestions: [
      {
        question: "",
        answer: "",
        requireCapitalization: false,
      },
    ],
  };

    case "complete-the-sentences":
      return {
        sentenceCompletionQuestions: [
          { text: "", options: ["", ""], answer: "" },
        ],
      };

    case "crossword":
      return {
        crosswordData: {},
      };

    case "missing-alphabets": 
      return {
        missingAlphabetsQuestions: [
          { word: "", answer: [] },
        ],
      };

    case "sentence-jumbles":
      return {
        sentenceJumblesQuestions: [
          { jumbledWords: [], correctSentence: "" },
        ],
      };

    case "stressing-syllable-words":
  return {
    stressingSyllableQuestions: [
      { word: "", correctSyllable: "" },
    ],
  };


case "listening":
  return {
    listeningQuestions: [
      { wordListening: "" },
    ],
  };




    case "vocabulary-building":
      return {
        vocabularyQuestions: [
          { word: "", options: ["", ""], correctAnswer: "" },
        ],
      };

      case "pronunciation":
  return {
    pronunciationQuestions: [
      {
        textToPronounce: "",
        ipaSound: "",
        requireCapitalization: false,
      },
    ],
  };


    case "word-formation":
      return {
        wordFormationQuestions: [
          { baseWord: "", options: ["", ""], correctAnswers: [] },
        ],
      };

    case "word-jumbles":
      return {
        wordJumblesQuestions: [
          { jumbled: "", word: [] },
        ],
      };

    default:
      return {};
  }
};

/* ---------- Main Edit Component ---------- */
const EditLessonPage = ({ lessonId: propLessonId, courseSlug: propCourseSlug }) => {
  const navigate = useNavigate();
  const params = useParams();

  const effectiveCourseSlug = propCourseSlug || params.courseSlug;
  const lessonId = propLessonId || params.lessonId;
  const isNew = lessonId === "add" || propLessonId === "add";

  /* ---------- Load Course ---------- */
  const {
    data: courses,
    isLoading: courseLoading,
    error: courseError,
    refetch,
  } = useGetCourseBySlugQuery(effectiveCourseSlug, {
    skip: !effectiveCourseSlug,
    refetchOnMountOrArgChange: true,
  });

  const course = useMemo(
    () => (courses?.course ? courses.course : courses || null),
    [courses]
  );

  const existingLessonFromCourse = useMemo(() => {
    if (!course || isNew) return null;

    return (course.lessons || []).find(
      (l) =>
        String(l._id) === String(lessonId) ||
        String(l.id) === String(lessonId)
    );
  }, [course, lessonId, isNew]);

  const [addLesson, { isLoading: isAdding }] = useAddLessonMutation();
  const [editLesson, { isLoading: isUpdating }] = useEditLessonMutation();

  const [form, setForm] = useState({
    title: "",
    content: "",
    details: "",
    order: "",
    exercises: [],
  });

  const [errors, setErrors] = useState({});
  const [audioPreviews, setAudioPreviews] = useState({});
const [showSaved, setShowSaved] = useState(false);


useEffect(() => {
  return () => {
    setAudioPreviews((prev) => {
      Object.values(prev).forEach((url) => {
        if (typeof url === "string" && url.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }
      });
      return {};
    });
  };
}, []);


useEffect(() => {
  if (!existingLessonFromCourse) return;

  const previews = {};
  existingLessonFromCourse.exercises.forEach((ex, idx) => {
    if (typeof ex.audio === "string" && ex.audio.trim()) {
      previews[`exercise-${idx}`] = ex.audio;
    }
  });

  setAudioPreviews(previews);
}, [existingLessonFromCourse]);



useEffect(() => {
  if (courseLoading) return;

  if (isNew) {
    if (!effectiveCourseSlug) {
      toast.error("Cannot create a lesson without a course slug.");
      navigate("/admin/courses");
      return;
    }

    setForm({
      title: "",
      content: "",
      details: "",
      order: String((course?.lessons?.length || 0) + 1),
      exercises: [],
    });
    return;
  }

  if (existingLessonFromCourse) {
    const prepared = {
      title: existingLessonFromCourse.title || "",
      content: existingLessonFromCourse.content || "",
      details: existingLessonFromCourse.details || "",
      order: String(existingLessonFromCourse.order || ""),
    
     exercises: (existingLessonFromCourse.exercises || []).map((ex) => ({
  _id: ex._id || makeId(),
  type: ex.type ?? "",

  // GROUPED FIELDS
  title: ex.title || "",
  details: ex.details || "",
  audio: ex.audio || "",
  // COMMON FIELDS (MUST ALWAYS EXIST)
  sentence: ex.sentence || "",
  question: ex.question || "",
  image: ex.image || "",
  
matchingPairs: Array.isArray(ex.matchingPairs) ? ex.matchingPairs : [],

  categories: Array.isArray(ex.categories) ? ex.categories : [],
  words: Array.isArray(ex.words) ? ex.words : [],

multipleChoiceQuestions: Array.isArray(ex.multipleChoiceQuestions)
  ? ex.multipleChoiceQuestions.map((q) => ({
      question: q.question || "",
      options: Array.isArray(q.options) ? q.options : [],
      answer: q.answer || "",
      requireCapitalization: !!q.requireCapitalization,
    }))
  : [],

    // 🔥 MUST INCLUDE ALL SUB-QUESTION ARRAYS (never undefined)

    pronunciationQuestions:
    Array.isArray(ex.pronunciationQuestions)
      ? ex.pronunciationQuestions.map((q) => ({
          textToPronounce: q.textToPronounce || "",
          ipaSound: q.ipaSound || "",
          requireCapitalization: !!q.requireCapitalization,
        }))
      : [],


  fillInTheBlanksQuestions:
  Array.isArray(ex.fillInTheBlanksQuestions)
    ? ex.fillInTheBlanksQuestions.map((q) => ({
        sentence: q.sentence || "",
        answers: Array.isArray(q.answers) ? q.answers : [],
        image: q.image || "",
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [],

pictureFillInTheBlanksQuestions:
  Array.isArray(ex.pictureFillInTheBlanksQuestions)
    ? ex.pictureFillInTheBlanksQuestions.map((q) => ({
        sentence: q.sentence || "",
        answers: Array.isArray(q.answers) ? q.answers : [],
        image: q.image || "",
      }))
    : [],

    dragAndDropQuestions:
  Array.isArray(ex.dragAndDropQuestions)
    ? ex.dragAndDropQuestions.map((q) => ({
        sentence: q.sentence || "",
        options: Array.isArray(q.options) ? q.options : [],
        answer: Array.isArray(q.answer) ? q.answer : [],
      }))
    : [],

   
wordChoiceQuestions:
  Array.isArray(ex.wordChoiceQuestions)
    ? ex.wordChoiceQuestions.map((q) => ({
        sentence: q.sentence || "",
        options: Array.isArray(q.options) ? q.options : [],
        correct: q.correct || "",
      }))
    : [],


 conversationQuestions: Array.isArray(ex.conversationQuestions)
  ? ex.conversationQuestions.map((q) => ({
      question: q.question ?? q.prompt ?? "",
      options: Array.isArray(q.options) ? q.options : [],
      answer: q.answer || "",
      requireCapitalization: !!q.requireCapitalization,
    }))
  : [],



 sentenceCompletionQuestions:
  Array.isArray(ex.sentenceCompletionQuestions)
    ? ex.sentenceCompletionQuestions.map((q) => ({
        text: q.text || "",
        options: Array.isArray(q.options) ? q.options : [],
        answer: q.answer || "",
      }))
    : [],


missingAlphabetsQuestions:
  Array.isArray(ex.missingAlphabetsQuestions)
    ? ex.missingAlphabetsQuestions.map((q) => ({
        word: q.word || "",
        answer: Array.isArray(q.answer) ? q.answer : [],
      }))
    : [],


sentenceJumblesQuestions:
  Array.isArray(ex.sentenceJumblesQuestions)
    ? ex.sentenceJumblesQuestions.map((q) => ({
        correctSentence: q.correctSentence || "",
        jumbledWords: Array.isArray(q.jumbledWords) ? q.jumbledWords : [],
      }))
    : [],

stressingSyllableQuestions:
  Array.isArray(ex.stressingSyllableQuestions)
    ? ex.stressingSyllableQuestions.map((q) => ({
        word: q.word || "",
        correctSyllable:
          typeof q.correctSyllable === "string"
            ? q.correctSyllable
            : "",
      }))
    : [],


vocabularyQuestions:
  Array.isArray(ex.vocabularyQuestions)
    ? ex.vocabularyQuestions.map((q) => ({
        word: q.word || "",
        options: Array.isArray(q.options) ? q.options : [],
        correctAnswer: q.correctAnswer || "",
      }))
    : [],


wordFormationQuestions:
  Array.isArray(ex.wordFormationQuestions)
    ? ex.wordFormationQuestions.map((q) => ({
        baseWord: q.baseWord || "",
        options: Array.isArray(q.options) ? q.options : [],
        correctAnswers: Array.isArray(q.correctAnswers) ? q.correctAnswers : [],
      }))
    : [],

wordJumblesQuestions:
  Array.isArray(ex.wordJumblesQuestions)
    ? ex.wordJumblesQuestions.map((q) => ({
        jumbled: q.jumbled || "",
        word: Array.isArray(q.word) ? q.word : [],
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [],


listeningQuestions: Array.isArray(ex.listeningQuestions)
  ? ex.listeningQuestions.map((q) => ({
      wordListening: q.wordListening || "",
    }))
  : [],



  // CROSSWORD SPECIAL CASE (object, not array)
  crosswordData: ex.crosswordData ?? {},
}))

    };

    setForm(prepared);
  }
}, [course, existingLessonFromCourse, isNew, courseLoading, effectiveCourseSlug, navigate]);

/* ---------- Basic field update (NO sanitization here) ---------- */
const handleField = useCallback((field, value) => {
  setForm((prev) => ({
    ...prev,
    [field]: value, 
  }));

  setErrors((prev) => ({
    ...prev,
    [field]: null,
  }));
}, []);



const removeAudio = useCallback((exerciseIndex) => {
  const key = `exercise-${exerciseIndex}`;

  // remove preview
  setAudioPreviews((prev) => {
    const updated = { ...prev };

    if (updated[key]?.startsWith("blob:")) {
      URL.revokeObjectURL(updated[key]);
    }

    delete updated[key];
    return updated;
  });

  // remove from form (IMPORTANT)
  setForm((prev) => {
    const exercises = [...prev.exercises];
    exercises[exerciseIndex] = {
      ...exercises[exerciseIndex],
      audio: "", 
    };
    return { ...prev, exercises };
  });
}, []);




/* ---------- Exercise field update ---------- */
const handleExerciseChange = useCallback((index, field, rawValue) => {
  setForm((prev) => {
    const exercises = [...prev.exercises];
    const current = exercises[index];

    if (!current) return prev;

    // sanitize string values
  const value = rawValue;



    let updatedExercise = {
      ...current,
      [field]: value,
    };

    /* --------------------------------
       TYPE CHANGE = FULL RESET (SAFE)
    -------------------------------- */
    if (field === "type") {
      // 🔥 clear audio preview when type changes
      setAudioPreviews((prevPreviews) => {
        const key = `exercise-${index}`;
        const copy = { ...prevPreviews };

        if (copy[key]?.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(copy[key]);
          } catch {}
        }

        delete copy[key];
        return copy;
      });

      updatedExercise = {
        _id: current._id,
        type: value,

        /* ---------- SHARED (GROUPED) ---------- */
        title: current.title || "",
        details: current.details || "",
        audio: current.audio || "",
        image: current.image || "",

        /* ---------- COMMON ---------- */
        question: "",
        sentence: "",

        /* ---------- ARRAYS ---------- */
     
        matchingPairs: [],
        words: [],
        categories: [],

        /* ---------- QUESTION ARRAYS ---------- */
        fillInTheBlanksQuestions: [],
        pictureFillInTheBlanksQuestions: [],
        dragAndDropQuestions: [],
        wordChoiceQuestions: [],
        conversationQuestions: [],
        sentenceCompletionQuestions: [],
        missingAlphabetsQuestions: [],
        sentenceJumblesQuestions: [],
        stressingSyllableQuestions: [],
        vocabularyQuestions: [],
        wordFormationQuestions: [],
        wordJumblesQuestions: [],
        pronunciationQuestions: [],
        listeningQuestions: [],

        /* ---------- SPECIAL ---------- */
        crosswordData: {},

        /* ---------- TYPE DEFAULTS ---------- */
        ...getExerciseDefaultsByType(value),
      };
    }

    exercises[index] = updatedExercise;

    return {
      ...prev,
      exercises,
    };
  });

  /* --------------------------------
     CLEAR ERRORS FOR THIS EXERCISE
  -------------------------------- */
  setErrors((prev) => {
    const cleaned = { ...prev };
    Object.keys(cleaned).forEach((key) => {
      if (key.startsWith(`exercise-${index}-`)) {
        delete cleaned[key];
      }
    });
    return cleaned;
  });
}, []);



const addExercise = useCallback(() => {
  setForm((prev) => ({
    ...prev,
    exercises: [
      ...prev.exercises,
      {
        // ❗ frontend-only id (Mongo will generate later)
        _id: makeId(),

        /* ---------------- TYPE ---------------- */
        type: "multiple-choice",

        /* ---------------- GROUP FIELDS ---------------- */
        title: "",
        details: "",

        /* ---------------- MULTIPLE CHOICE ---------------- */
        multipleChoiceQuestions: [
          {
            question: "",
            options: ["", ""],
            answer: "",
            requireCapitalization: false,
          },
        ],

        /* ---------------- COMMON MEDIA ---------------- */
        image: "",
        audio: "",

        /* ---------------- ALWAYS-DEFINED ARRAYS ---------------- */
        fillInTheBlanksQuestions: [],
        pictureFillInTheBlanksQuestions: [],
        matchingPairs: [],
        dragAndDropQuestions: [],
        words: [],
        categories: [],

        wordChoiceQuestions: [],
        conversationQuestions: [],
        sentenceCompletionQuestions: [],
        missingAlphabetsQuestions: [],
        sentenceJumblesQuestions: [],
        stressingSyllableQuestions: [],
        vocabularyQuestions: [],
        wordFormationQuestions: [],
        wordJumblesQuestions: [],
        pronunciationQuestions: [],
        listeningQuestions: [],

        /* ---------------- SPECIAL ---------------- */
        crosswordData: {},
      },
    ],
  }));
}, []);



const removeExercise = useCallback((index) => {
  setForm((prev) => {
    const updated = [...prev.exercises];
    const removed = updated[index];
    updated.splice(index, 1);
    return { ...prev, exercises: updated };
  });

  /* -----------------------------
     FIX ERRORS (REINDEX SAFE)
  ------------------------------ */
  setErrors((prev) => {
    const cleaned = {};

    Object.keys(prev).forEach((key) => {
      if (!key.startsWith("exercise-")) {
        cleaned[key] = prev[key];
        return;
      }

      const match = key.match(/^exercise-(\d+)-(.*)$/);
      if (!match) return;

      const errIndex = Number(match[1]);
      const field = match[2];

      if (errIndex === index) return;

      if (errIndex > index) {
        cleaned[`exercise-${errIndex - 1}-${field}`] = prev[key];
      } else {
        cleaned[key] = prev[key];
      }
    });

    return cleaned;
  });

  /* -----------------------------
     FIX AUDIO PREVIEWS (GROUP)
  ------------------------------ */

  setAudioPreviews((prev) => {
  const cleaned = { ...prev };

  Object.keys(cleaned).forEach((k) => {
   if (k === `exercise-${index}`)
 {
      if (cleaned[k]?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(cleaned[k]);
        } catch {}
      }
      delete cleaned[k];
    }
  });

  return cleaned;
});


}, []);




/* ---------- VALIDATION ---------- */
const validateForm = useCallback(() => {
  const newErrors = {};

  const isFile = (v) => typeof File !== "undefined" && v instanceof File;
  const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  const startsCap = (v) => typeof v === "string" && /^[A-Z]/.test(v.trim());

  /* ------------------------------
     LESSON LEVEL VALIDATION
  ------------------------------ */
  if (!form.title?.trim()) newErrors.title = "Lesson title is required";
  if (!form.order || Number(form.order) < 1)
    newErrors.order = "Order must be a positive number";

  /* ------------------------------
     GROUP VALIDATION (shared fields)
  ------------------------------ */
  const grouped = {};

form.exercises.forEach((ex, idx) => {
  if (ex.type === "exercise-section") {
    if (!ex.title?.trim()) {
      newErrors[`exercise-${idx}-title`] = "Section title is required";
    }
  }
  if (!ex.type) return;
  grouped[ex.type] ??= [];
  grouped[ex.type].push({ ...ex, index: idx });
});

 /* ------------------------------
   GROUP TITLE / DETAILS VALIDATION
------------------------------ */
Object.entries(grouped).forEach(([type, list]) => {
if (type === "exercise-section" || !list.length) return;

  const first = list[0];

  if (!first.title?.trim()) {
    newErrors[`group-${type}-title`] = "Exercise title is required";
  }
});


/* ------------------------------
   AUDIO VALIDATION (ALL EXERCISES)
------------------------------ */
form.exercises.forEach((ex, idx) => {
  if (ex.audio && isFile(ex.audio) && ex.audio.type !== "audio/mpeg") {
    newErrors[`exercise-${idx}-audio`] = "Only MP3 format allowed";
  }
});


  /* ------------------------------
     EXERCISE–LEVEL VALIDATION
  ------------------------------ */
  form.exercises.forEach((ex, idx) => {
    const key = (f) => `exercise-${idx}-${f}`;

    if (!ex?.type) {
      newErrors[key("type")] = "Exercise type missing";
      return;
    }

    switch (ex.type) {
      /* ----------------------------------------------------
         1. MULTIPLE CHOICE
      ---------------------------------------------------- */
     case "multiple-choice":
  if (
    !Array.isArray(ex.multipleChoiceQuestions) ||
    !ex.multipleChoiceQuestions.length
  ) {
    newErrors[key("questions")] =
      "At least one multiple-choice question is required";
    break;
  }

  ex.multipleChoiceQuestions.forEach((q, qIdx) => {
    /* QUESTION */
    if (!q.question?.trim()) {
      newErrors[
        `exercise-${idx}-mc-question-${qIdx}`
      ] = "Question required";
    }

    /* OPTIONS */
    if (!Array.isArray(q.options) || q.options.length < 2) {
      newErrors[
        `exercise-${idx}-mc-options-${qIdx}`
      ] = "At least two options required";
    }

    /* ANSWER */
    if (!q.answer?.trim()) {
      newErrors[
        `exercise-${idx}-mc-answer-${qIdx}`
      ] = "Correct answer required";
    }

    /* CAPITALIZATION RULE */
    if (q.requireCapitalization && q.answer) {
      if (!/^[A-Z]/.test(q.answer.trim())) {
        newErrors[
          `exercise-${idx}-mc-cap-${qIdx}`
        ] = "Answer must start with a capital letter";
      }
    }
  });

  break;


      /* ----------------------------------------------------
         2. FILL IN THE BLANKS
      ---------------------------------------------------- */
case "fill-in-the-blanks":
  if (!ex.fillInTheBlanksQuestions?.length) {
    newErrors[key("questions")] = "At least one question required";
  }

  ex.fillInTheBlanksQuestions.forEach((q, qIdx) => {
    /* ----------------------------
       Sentence validation
    ----------------------------- */
    if (!q.sentence?.trim()) {
      newErrors[`exercise-${idx}-sentence-${qIdx}`] =
        "Sentence required";
      return;
    }

    const blanks = (q.sentence.match(/_+/g) || []).length;
    if (!blanks) {
      newErrors[`exercise-${idx}-sentence-${qIdx}`] =
        "Sentence must contain at least one _ blank";
      return;
    }

    /* ----------------------------
       Answers validation
    ----------------------------- */
    if (!Array.isArray(q.answers)) {
      newErrors[`exercise-${idx}-answers-${qIdx}`] =
        "Answers must be provided";
      return;
    }

    const answers = q.answers.map((a) => a?.trim()).filter(Boolean);

    if (answers.length !== blanks) {
      newErrors[`exercise-${idx}-answers-${qIdx}`] =
        "Answers must match number of blanks";
      return;
    }

    /* ----------------------------
       ✅ CAPITALIZATION (PER QUESTION)
    ----------------------------- */
    if (q.requireCapitalization) {
      answers.forEach((a, aIdx) => {
        if (!/^[A-Z]/.test(a)) {
          newErrors[
            `exercise-${idx}-answers-${qIdx}-cap-${aIdx}`
          ] = "Answer must start with a capital letter";
        }
      });
    }
  });
  break;

      /* ----------------------------------------------------
         3. PICTURE FILL IN THE BLANKS
      ---------------------------------------------------- */
     case "picture-fill-in-the-blanks":
  if (!ex.pictureFillInTheBlanksQuestions?.length) {
    newErrors[key("questions")] = "At least one question required";
  }

  ex.pictureFillInTheBlanksQuestions.forEach((q, qIdx) => {
    if (!q.sentence?.trim()) {
      newErrors[`exercise-${idx}-pftb-sentence-${qIdx}`] =
        "Sentence required";
      return;
    }

    const blanks = (q.sentence.match(/_+/g) || []).length;
    if (!blanks) {
      newErrors[`exercise-${idx}-pftb-sentence-${qIdx}`] =
        "Sentence must contain blanks (_)";
      return;
    }

    if (!q.image) {
      newErrors[`exercise-${idx}-pftb-image-${qIdx}`] =
        "Image required";
      return;
    }

    if (!Array.isArray(q.answers)) {
      newErrors[`exercise-${idx}-pftb-answers-${qIdx}`] =
        "Answers required";
      return;
    }

    const answers = q.answers.map(a => a?.trim()).filter(Boolean);

    if (answers.length !== blanks) {
      newErrors[`exercise-${idx}-pftb-answers-${qIdx}`] =
        "Answers must match number of blanks";
    }
  });
  break;



      /* ----------------------------------------------------
         4. MATCHING
      ---------------------------------------------------- */
      case "matching":
  if (!Array.isArray(ex.matchingPairs) || !ex.matchingPairs.length) {
    newErrors[key("pairs")] = "At least one pair required";
    break;
  }

  ex.matchingPairs.forEach((p, pIdx) => {
    if (!p.question?.trim())
      newErrors[`exercise-${idx}-pair-${pIdx}-q`] = "Question required";
    if (!p.answer?.trim())
      newErrors[`exercise-${idx}-pair-${pIdx}-a`] = "Answer required";
  });
  break;


      /* ----------------------------------------------------
         5. LISTENING
      ---------------------------------------------------- */
    case "listening":
  if (
    !Array.isArray(ex.listeningQuestions) ||
    !ex.listeningQuestions.length
  ) {
    newErrors[key("questions")] =
      "At least one listening word is required";
    break;
  }

  ex.listeningQuestions.forEach((q, qIdx) => {
  if (!q.wordListening?.trim()) {
  newErrors[`exercise-${idx}-wordListening-${qIdx}`] =
    "Word or sentence to speak is required";
}

  });
  break;



      /* ----------------------------------------------------
         6. DRAG AND DROP
      ---------------------------------------------------- */
      case "drag-and-drop":
  if (!Array.isArray(ex.dragAndDropQuestions) || !ex.dragAndDropQuestions.length) {
    newErrors[key("questions")] = "At least one question required";
    break;
  }

  ex.dragAndDropQuestions.forEach((q, qIdx) => {
    if (!q.sentence?.trim())
      newErrors[`exercise-${idx}-dd-sentence-${qIdx}`] =
        "Sentence required";

    if (!q.options?.length)
      newErrors[`exercise-${idx}-dd-options-${qIdx}`] =
        "You must add draggable words";

    if (!q.answer?.length)
      newErrors[`exercise-${idx}-dd-answer-${qIdx}`] =
        "Correct answer required";
  });
  break;

      /* ----------------------------------------------------
         7. CATEGORIZING WORDS
      ---------------------------------------------------- */
      case "categorizing-words":
  if (!Array.isArray(ex.categories) || ex.categories.filter(Boolean).length < 2) {
    newErrors[key("categories")] =
      "At least two categories are required";
  }

  if (!Array.isArray(ex.words) || !ex.words.length) {
    newErrors[key("words")] = "At least one word is required";
  } else {
    ex.words.forEach((w, wIdx) => {
      if (!w.word?.trim()) {
        newErrors[`exercise-${idx}-word-${wIdx}`] =
          "Word text is required";
      }
      if (!w.category?.trim()) {
        newErrors[`exercise-${idx}-word-cat-${wIdx}`] =
          "Category is required";
      }
    });
  }
  break;


      /* ----------------------------------------------------
         8. CIRCLE THE CORRECT WORD
      ---------------------------------------------------- */
      case "circle-the-correct-word":
        ex.wordChoiceQuestions?.forEach((q, qIdx) => {
          if (!q.sentence?.trim())
            newErrors[`exercise-${idx}-wc-sentence-${qIdx}`] = "Sentence required";

          if (!q.options?.length)
            newErrors[`exercise-${idx}-wc-options-${qIdx}`] = "Options required";

          if (!q.correct?.trim())
            newErrors[`exercise-${idx}-wc-correct-${qIdx}`] = "Correct answer required";
        });
        break;

      /* ----------------------------------------------------
         9. COMPLETE THE CONVERSATION
      ---------------------------------------------------- */

      case "complete-the-conversation":
  if (
    !Array.isArray(ex.conversationQuestions) ||
    !ex.conversationQuestions.length
  ) {
    newErrors[key("questions")] = "At least one conversation line required";
    break;
  }
  ex.conversationQuestions.forEach((q, qIdx) => {
    if (!q.question?.trim()) {
      newErrors[`exercise-${idx}-conv-question-${qIdx}`] =
        "Conversation text is required";
    }
    if (!q.answer?.trim()) {
      newErrors[`exercise-${idx}-conv-answer-${qIdx}`] =
        "Correct answer (what student should type) is required";
    }
  });
  break;


      /* ----------------------------------------------------
         10. COMPLETE THE SENTENCES
      ---------------------------------------------------- */
      case "complete-the-sentences":
        ex.sentenceCompletionQuestions?.forEach((q, qIdx) => {
          if (!q.text?.trim())
            newErrors[`exercise-${idx}-sc-text-${qIdx}`] = "Sentence required";

          if (!q.options?.length)
            newErrors[`exercise-${idx}-sc-options-${qIdx}`] = "Options required";

          if (!q.answer?.trim())
            newErrors[`exercise-${idx}-sc-answer-${qIdx}`] =
              "Correct answer required";
        });
        break;

      /* ----------------------------------------------------
         11. CROSSWORD
      ---------------------------------------------------- */
      case "crossword":
        if (!ex.crosswordData || !Object.keys(ex.crosswordData).length)
          newErrors[key("crosswordData")] = "Crossword data required";
        break;

      /* ----------------------------------------------------
         12. MISSING ALPHABETS
      ---------------------------------------------------- */
      case "missing-alphabets":
        ex.missingAlphabetsQuestions?.forEach((q, qIdx) => {
          if (!q.word?.trim())
            newErrors[`exercise-${idx}-ma-word-${qIdx}`] = "Word required";

          if (!q.answer?.length)
            newErrors[`exercise-${idx}-ma-answer-${qIdx}`] = "Missing letters required";
        });
        break;

      /* ----------------------------------------------------
         13. SENTENCE JUMBLES
      ---------------------------------------------------- */
      case "sentence-jumbles":
        ex.sentenceJumblesQuestions?.forEach((q, qIdx) => {
          if (!q.correctSentence?.trim())
            newErrors[`exercise-${idx}-sj-correct-${qIdx}`] =
              "Correct sentence required";

          if (!q.jumbledWords?.length)
            newErrors[`exercise-${idx}-sj-jumbled-${qIdx}`] =
              "Jumbled words required";
        });
        break;

      /* ----------------------------------------------------
         14. STRESSING SYLLABLE WORDS
      ---------------------------------------------------- */
    case "stressing-syllable-words":
  ex.stressingSyllableQuestions?.forEach((q, qIdx) => {
    if (!q.word?.trim()) {
      newErrors[`exercise-${idx}-ss-word-${qIdx}`] =
        "Word required";
    }

    if (!q.correctSyllable || !q.correctSyllable.trim()) {
      newErrors[`exercise-${idx}-ss-correct-${qIdx}`] =
        "Stressed syllable is required";
    }
  });
  break;



      /* ----------------------------------------------------
         15. VOCABULARY BUILDING
      ---------------------------------------------------- */
      case "vocabulary-building":
        ex.vocabularyQuestions?.forEach((q, qIdx) => {
          if (!q.word?.trim())
            newErrors[`exercise-${idx}-vb-word-${qIdx}`] = "Word required";

          if (!q.options?.length)
            newErrors[`exercise-${idx}-vb-options-${qIdx}`] = "Options required";

          if (!q.correctAnswer?.trim())
            newErrors[`exercise-${idx}-vb-correct-${qIdx}`] =
              "Correct answer required";
        });
        break;

      /* ----------------------------------------------------
         16. WORD FORMATION
      ---------------------------------------------------- */
      case "word-formation":
        ex.wordFormationQuestions?.forEach((q, qIdx) => {
          if (!q.baseWord?.trim())
            newErrors[`exercise-${idx}-wf-base-${qIdx}`] = "Base word required";

          if (!q.options?.length)
            newErrors[`exercise-${idx}-wf-options-${qIdx}`] = "Options required";

          if (!q.correctAnswers?.length)
            newErrors[`exercise-${idx}-wf-correct-${qIdx}`] =
              "Correct answers required";
        });
        break;

      /* ----------------------------------------------------
         17. WORD JUMBLES
      ---------------------------------------------------- */
      case "word-jumbles":
        ex.wordJumblesQuestions?.forEach((q, qIdx) => {
          if (!q.jumbled?.trim())
            newErrors[`exercise-${idx}-wj-jumbled-${qIdx}`] = "Jumbled word required";

          if (!q.word?.length)
            newErrors[`exercise-${idx}-wj-word-${qIdx}`] = "Correct word required";
        });
        break;

      /* ----------------------------------------------------
         18. PRONUNCIATION
      ---------------------------------------------------- */
     case "pronunciation":
  if (
    !Array.isArray(ex.pronunciationQuestions) ||
    !ex.pronunciationQuestions.length
  ) {
    newErrors[key("questions")] =
      "At least one pronunciation item required";
    break;
  }

  ex.pronunciationQuestions.forEach((q, qIdx) => {
    if (!q.textToPronounce?.trim()) {
      newErrors[
        `exercise-${idx}-pron-text-${qIdx}`
      ] = "Text to pronounce required";
    }

    if (!q.ipaSound?.trim()) {
      newErrors[
        `exercise-${idx}-pron-ipa-${qIdx}`
      ] = "IPA sound (right answer) required";
    }
  });
  break;


      default:
        break;

    }
  });

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}, [form]);


/* ---------- Build Payload ---------- */
const buildLessonPayload = useCallback(() => {
  const safeTrim = (v) => (typeof v === "string" ? v.trim() : v);
  const clean = (v) =>
    typeof v === "string" ? DOMPurify.sanitize(v).trim() : v;
  const isFile = (v) =>
    typeof File !== "undefined" && v instanceof File;


  /* -----------------------------------------------------
     Shape + sanitize exercises properly
  ----------------------------------------------------- */


const isValidObjectId = (id) =>
  typeof id === "string" && /^[a-f\d]{24}$/i.test(id);

const shapedExercises = form.exercises.map((ex) => {
const shaped = {
  /* ---------------------------------------
     CORE (safe for all exercise types)
  --------------------------------------- */
  type: ex.type,

  title: clean(ex.title || ""),
  details: clean(ex.details || ""),
  question: clean(ex.question || ""),
  sentence: clean(ex.sentence || ""),

  image: typeof ex.image === "string" ? ex.image : "",
  audio:
    ex.audio === ""
      ? ""
      : typeof ex.audio === "string"
      ? ex.audio
      : undefined,
};


if (
  ex.type !== "listening" &&
  ex.type !== "multiple-choice"
) {
  shaped.answer = Array.isArray(ex.answer)
    ? ex.answer.map(clean)
    : [];
}


  /* ---------------------------------------
     ✅ KEEP _id ONLY IF VALID MONGO OBJECTID
  --------------------------------------- */
  if (isValidObjectId(ex._id)) {
    shaped._id = ex._id;
  }

  /* ---------------------------------------
     TYPE-SPECIFIC SHAPING
  --------------------------------------- */
  switch (ex.type) {
  /* --------------------------------
     SECTION / GROUP
  -------------------------------- */
  case "exercise-section":
    break;

  /* --------------------------------
     MULTIPLE CHOICE
  -------------------------------- */
  case "multiple-choice":
  shaped.multipleChoiceQuestions = ex.multipleChoiceQuestions.map(q => ({
    question: clean(q.question),
    options: q.options.map(clean),
    answer: clean(q.answer),
    requireCapitalization: !!q.requireCapitalization,
  }));
  break;

  /* --------------------------------
     FILL IN THE BLANKS
  -------------------------------- */
  case "fill-in-the-blanks":
    shaped.fillInTheBlanksQuestions = Array.isArray(
      ex.fillInTheBlanksQuestions
    )
      ? ex.fillInTheBlanksQuestions.map((q) => ({
          sentence: clean(q.sentence || ""),
          answers: Array.isArray(q.answers)
            ? q.answers.map(clean).filter(Boolean)
            : [],
          image: typeof q.image === "string" ? q.image : "",
          requireCapitalization: !!q.requireCapitalization,
        }))
      : [];
    break;

  /* --------------------------------
     PICTURE FILL IN THE BLANKS
  -------------------------------- */
  case "picture-fill-in-the-blanks":
    shaped.pictureFillInTheBlanksQuestions = Array.isArray(
      ex.pictureFillInTheBlanksQuestions
    )
      ? ex.pictureFillInTheBlanksQuestions.map((q) => ({
          sentence: clean(q.sentence || ""),
          answers: Array.isArray(q.answers)
            ? q.answers.map(clean).filter(Boolean)
            : [],
          image: typeof q.image === "string" ? q.image : "",
        }))
      : [];
    break;

  /* --------------------------------
     MATCHING
  -------------------------------- */
  case "matching":
    shaped.matchingPairs = Array.isArray(ex.matchingPairs)
      ? ex.matchingPairs.map((p) => ({
          question: clean(p.question || ""),
          answer: clean(p.answer || ""),
        }))
      : [];
    break;

  /* --------------------------------
     PRONUNCIATION
  -------------------------------- */
case "pronunciation":
  shaped.pronunciationQuestions = Array.isArray(
    ex.pronunciationQuestions
  )
    ? ex.pronunciationQuestions.map((q) => ({
        textToPronounce: clean(q.textToPronounce || ""),
        ipaSound: clean(q.ipaSound || ""),
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [];
  break;


  /* --------------------------------
     LISTENING
  -------------------------------- */
case "listening":
  shaped.listeningQuestions = Array.isArray(ex.listeningQuestions)
    ? ex.listeningQuestions.map((q) => ({
        wordListening: clean(q.wordListening || ""),
      }))
    : [];
  break;




  /* --------------------------------
     DRAG & DROP
  -------------------------------- */

  case "drag-and-drop":
    shaped.dragAndDropQuestions = Array.isArray(ex.dragAndDropQuestions)
      ? ex.dragAndDropQuestions.map((q) => ({
          sentence: clean(q.sentence || ""),
          options: Array.isArray(q.options)
            ? q.options.map(clean)
            : [],
          answer: Array.isArray(q.answer)
            ? q.answer.map(clean)
            : [],
        }))
      : [];
    break;

  /* --------------------------------
     CATEGORIZING WORDS
  -------------------------------- */
 case "categorizing-words":
  shaped.categories = Array.isArray(ex.categories)
    ? ex.categories.map(clean).filter(Boolean)
    : [];

  shaped.words = Array.isArray(ex.words)
    ? ex.words.map((w) => ({
        word: clean(w.word || ""),
        category: clean(w.category || ""),
      }))
    : [];
  break;


  /* --------------------------------
     CIRCLE THE CORRECT WORD
  -------------------------------- */
  case "circle-the-correct-word":
    shaped.wordChoiceQuestions = Array.isArray(ex.wordChoiceQuestions)
      ? ex.wordChoiceQuestions.map((q) => ({
          sentence: clean(q.sentence || ""),
          options: Array.isArray(q.options)
            ? q.options.map(clean)
            : [],
          correct: clean(q.correct || ""),
        }))
      : [];
    break;

  /* --------------------------------
     COMPLETE THE CONVERSATION  ✅ (FIX)
  -------------------------------- */

  case "complete-the-conversation":
  shaped.conversationQuestions = Array.isArray(ex.conversationQuestions)
    ? ex.conversationQuestions.map((q) => ({
        question: clean(q.question || ""),
        answer: clean(q.answer || ""),
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [];
  break;


  /* --------------------------------
     COMPLETE THE SENTENCES
  -------------------------------- */
  case "complete-the-sentences":
    shaped.sentenceCompletionQuestions = Array.isArray(
      ex.sentenceCompletionQuestions
    )
      ? ex.sentenceCompletionQuestions.map((q) => ({
          text: clean(q.text || ""),
          options: Array.isArray(q.options)
            ? q.options.map(clean)
            : [],
          answer: clean(q.answer || ""),
        }))
      : [];
    break;

  /* --------------------------------
     CROSSWORD
  -------------------------------- */
  case "crossword":
    shaped.crosswordData = ex.crosswordData || {};
    break;

  /* --------------------------------
     MISSING ALPHABETS
  -------------------------------- */
  case "missing-alphabets":
    shaped.missingAlphabetsQuestions = Array.isArray(
      ex.missingAlphabetsQuestions
    )
      ? ex.missingAlphabetsQuestions.map((q) => ({
          word: clean(q.word || ""),
          answer: Array.isArray(q.answer)
            ? q.answer.map(clean)
            : [],
        }))
      : [];
    break;

  /* --------------------------------
     SENTENCE JUMBLES
  -------------------------------- */
  case "sentence-jumbles":
    shaped.sentenceJumblesQuestions = Array.isArray(
      ex.sentenceJumblesQuestions
    )
      ? ex.sentenceJumblesQuestions.map((q) => ({
          correctSentence: clean(q.correctSentence || ""),
          jumbledWords: Array.isArray(q.jumbledWords)
            ? q.jumbledWords.map(clean)
            : [],
        }))
      : [];
    break;

  /* --------------------------------
     STRESSING SYLLABLE WORDS
  -------------------------------- */
 case "stressing-syllable-words":
  shaped.stressingSyllableQuestions = Array.isArray(
    ex.stressingSyllableQuestions
  )
    ? ex.stressingSyllableQuestions.map((q) => ({
        word: clean(q.word || ""),
        correctSyllable: clean(q.correctSyllable || ""),
      }))
    : [];
  break;


  /* --------------------------------
     VOCABULARY BUILDING
  -------------------------------- */
  case "vocabulary-building":
    shaped.vocabularyQuestions = Array.isArray(ex.vocabularyQuestions)
      ? ex.vocabularyQuestions.map((q) => ({
          word: clean(q.word || ""),
          options: Array.isArray(q.options)
            ? q.options.map(clean)
            : [],
          correctAnswer: clean(q.correctAnswer || ""),
        }))
      : [];
    break;

  /* --------------------------------
     WORD FORMATION
  -------------------------------- */
  case "word-formation":
    shaped.wordFormationQuestions = Array.isArray(
      ex.wordFormationQuestions
    )
      ? ex.wordFormationQuestions.map((q) => ({
          baseWord: clean(q.baseWord || ""),
          options: Array.isArray(q.options)
            ? q.options.map(clean)
            : [],
          correctAnswers: Array.isArray(q.correctAnswers)
            ? q.correctAnswers.map(clean)
            : [],
        }))
      : [];
    break;

  /* --------------------------------
     WORD JUMBLES
  -------------------------------- */
  case "word-jumbles":
    shaped.wordJumblesQuestions = Array.isArray(ex.wordJumblesQuestions)
      ? ex.wordJumblesQuestions.map((q) => ({
          jumbled: clean(q.jumbled || ""),
          word: Array.isArray(q.word)
            ? q.word.map(clean)
            : [],
            requireCapitalization: !!q.requireCapitalization,
        }))
      : [];
    break;

  default:
    break;
}

  return shaped;
});





  const exercisesJson = JSON.stringify(shapedExercises);

  /* -----------------------------------------------------
     Detect files for ALL exercise types
  ----------------------------------------------------- */

  const hasFiles = form.exercises.some((ex) =>
    isFile(ex.image) ||
    isFile(ex.audio) ||

    ex.fillInTheBlanksQuestions?.some((q) => isFile(q.image)) ||
    ex.wordChoiceQuestions?.some((q) => isFile(q.image)) ||
    ex.conversationQuestions?.some((q) => isFile(q.image)) ||
    ex.sentenceCompletionQuestions?.some((q) => isFile(q.image)) ||
    ex.missingAlphabetsQuestions?.some((q) => isFile(q.image)) ||
    ex.vocabularyQuestions?.some((q) => isFile(q.image)) ||
    ex.wordFormationQuestions?.some((q) => isFile(q.image)) ||
    ex.wordJumblesQuestions?.some((q) => isFile(q.image))
  );

  /* -----------------------------------------------------
     SEND NORMAL JSON WHEN NO FILES
  ----------------------------------------------------- */
  if (!hasFiles) {
    return {
      title: clean(form.title),
      content: DOMPurify.sanitize(form.content || ""),
      details: clean(form.details || ""),
      order: Number(form.order) || 1,
      exercises: shapedExercises,
    };
  }

  /* -----------------------------------------------------
     SEND FORM DATA FOR FILE UPLOADS
  ----------------------------------------------------- */
  const fd = new FormData();

  fd.append("title", clean(form.title || ""));
  fd.append("content", DOMPurify.sanitize(form.content || ""));
  fd.append("details", clean(form.details || ""));
  fd.append("order", String(Number(form.order) || 1));
  fd.append("exercises", exercisesJson);

  form.exercises.forEach((ex, i) => {
    if (isFile(ex.image)) fd.append(`exercise_${i}_image`, ex.image);
    if (isFile(ex.audio)) fd.append(`exercise_${i}_audio`, ex.audio);

    const appendNestedImage = (q, qIdx) => {
      if (isFile(q.image)) {
        fd.append(`exercise_${i}_q_${qIdx}_image`, q.image);
      }
    };

    ex.fillInTheBlanksQuestions?.forEach(appendNestedImage);
    ex.wordChoiceQuestions?.forEach(appendNestedImage);
    ex.conversationQuestions?.forEach(appendNestedImage);
    ex.sentenceCompletionQuestions?.forEach(appendNestedImage);
    ex.missingAlphabetsQuestions?.forEach(appendNestedImage);
    ex.vocabularyQuestions?.forEach(appendNestedImage);
    ex.wordFormationQuestions?.forEach(appendNestedImage);
    ex.wordJumblesQuestions?.forEach(appendNestedImage);
  });

  return fd;
}, [form]);





/* ---------- Submit ---------- */
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    toast.error("Please fix errors before submitting.");
    return;
  }

  try {
    const payload = buildLessonPayload();

    // ---------------- CREATE ----------------
    if (isNew) {
      const res = await addLesson({
        courseSlug: effectiveCourseSlug,
        lessonData: payload,
      }).unwrap();

      if (!res) throw new Error("Lesson creation failed");

      toast.success("Lesson created successfully!");
      navigate(`/admin/courses/${effectiveCourseSlug}/edit`);
      return;
    }

    // ---------------- UPDATE ----------------
const res = await editLesson({
  courseSlug: effectiveCourseSlug,
  lessonId,
  lessonData: payload,
}).unwrap();

if (!res) throw new Error("Lesson update failed");


toast.success("Lesson updated successfully!");
setShowSaved(true);
setTimeout(() => setShowSaved(false), 3000);

  } catch (err) {
    console.error("Submission error:", err);

    toast.error(
      err?.data?.message ||
        err?.message ||
        "Error saving lesson. Please try again."
    );
  }
};
 

/* ---------- Loading / Errors ---------- */
if (courseLoading) return <div>Loading...</div>;
if (!isNew && !existingLessonFromCourse)
  return <div>Error: lesson not found.</div>;

/* ---------- FINAL JSX ---------- */
return (
<div className="w-full">
      {showSaved && (
  <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded shadow">
    Lesson saved successfully ✅
  </div>
)}
<form className="m-0 p-0" onSubmit={handleSubmit}>

    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
  {/* LEFT: Title + Description */}
  <div>
    <CardTitle>
      {isNew ? "Create Lesson" : "Edit Lesson"}
    </CardTitle>
    <CardDescription>
      {isNew
        ? "Add a new lesson to the course."
        : "Update the lesson details and exercises."}
    </CardDescription>
  </div>

  {/* RIGHT: ACTION BUTTONS */}
  <div className="flex gap-2">
    <Button
      type="button"
      variant="secondary"
      onClick={() =>
        navigate(`/admin/courses/${effectiveCourseSlug}/edit`)
      }
    >
      Back
    </Button>

          <Button
            type="submit"
            disabled={
              isAdding ||
              isUpdating ||
              form.exercises.length === 0
            }
          >

      {isAdding || isUpdating
        ? "Saving..."
        : isNew
        ? "Create Lesson"
        : "Update Lesson"}
    </Button>
  </div>
</CardHeader>


    <CardContent className="pb-2">

          <StringField
            label="Lesson Title"
            id="title"
            value={form.title}
            onChange={(v) => handleField("title", v)}
            required
            error={errors.title}
          />

         <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label>Lesson Details</Label> </div>
                   </div>

           <RichTextEditor
            value={form.details}
            onChange={(v) => handleField("details", v)}
            placeholder="Lesson details"
          />


          <StringField
            label="Order"
            id="order"
            type="number"
            value={form.order}
            onChange={(v) => handleField("order", v)}
            required
            error={errors.order}
          />

          {/* ----------- EXERCISES ----------- */}
        <Card className="shadow-md my-2">
            <CardHeader>
              <CardTitle>Exercises</CardTitle>
              <CardDescription>
                Exercises of the same type are grouped
              </CardDescription>
            </CardHeader>

          <CardContent className="pb-2">
             {Object.keys(errors)
    .filter((k) => k.startsWith("group-"))
    .map((k) => (
      <p key={k} className="text-red-500 text-sm mb-2">
        {errors[k]}
      </p>
    ))}
                {form.exercises.map((ex, idx) => (
            <Card
              key={ex._id}
              className="p-4 space-y-4 border rounded shadow-sm"
              id={`exercise-${idx}`}
            >
              <h3 className="text-lg font-semibold">
            {ex.type === "exercise-section"
              ? "Section / Subtitle"
              : `Exercise ${idx + 1}`}
          </h3>


              {/* Exercise Type */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label>Exercise Type</Label>

                  <Select
                    value={ex.type}
                    onValueChange={(v) =>
                      handleExerciseChange(idx, "type", v)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>

                    <SelectContent>
                      {exerciseTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {formatExerciseType(t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {errors[`exercise-${idx}-type`] && (
                    <p className="text-red-500 text-sm">
                      {errors[`exercise-${idx}-type`]}
                    </p>
                  )}
                </div>

                {/* Remove button */}
                <Button
                  variant="destructive"
                  size="sm"
                  className="px-4"
                  onClick={() => removeExercise(idx)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              </div>

              
            <StringField
              label="Exercise Name (shown to students)"
              id={`exercise-title-${idx}`}
              value={ex.title || ""}
              onChange={(v) => handleExerciseChange(idx, "title", v)}
              placeholder="e.g. Choose the correct answer"
              required={ex.type !== "exercise-section"}
              error={errors[`exercise-${idx}-title`]}
            />

             {/* Exercise Instructions / Details */}
               <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label>Exercise Instructions</Label> </div>
                   </div>
              
                <RichTextEditor
                label="Exercise Instructions"
                id={`exercise-details-${idx}`}
                value={ex.details || ""}
                onChange={(v) => handleExerciseChange(idx, "details", v)}
                placeholder="Optional instructions for students"
                rows={3}
                error={errors[`exercise-${idx}-details`]}
              />



                            {/* Audio Explanation */}
              <div className="space-y-2">
                <Label>Audio Explanation (MP3)</Label>

                <Input
                  key={audioPreviews[`exercise-${idx}`] || "no-audio"}
                  type="file"
                  accept="audio/mpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    if (file.type !== "audio/mpeg") {
                      toast.error("Only MP3 allowed");
                      return;
                    }

                    const previewUrl = URL.createObjectURL(file);
                    const key = `exercise-${idx}`;

                    setAudioPreviews((prev) => {
                      if (prev[key]?.startsWith("blob:")) {
                        try {
                          URL.revokeObjectURL(prev[key]);
                        } catch {}
                      }
                      return {
                        ...prev,
                        [key]: previewUrl,
                      };
                    });

                    handleExerciseChange(idx, "audio", file);
                  }}
                />

                {(audioPreviews[`exercise-${idx}`] || ex.audio) && (
                  <div className="flex items-center gap-3">
                    <audio controls preload="metadata" className="flex-1">
                      <source
                        src={audioPreviews[`exercise-${idx}`] || ex.audio}
                        type="audio/mpeg"
                      />
                    </audio>

                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeAudio(idx)}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>




              {/* Exercise Fields */}
              <ExerciseFields
                type={ex.type}
                index={idx}
                exercise={ex}
                errors={errors}
                onUpdate={(field, v) =>
                  handleExerciseChange(idx, field, v)
                }
              />
            </Card>
          ))}

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={addExercise}
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Add Exercise
              </Button>
            </CardContent>
          </Card>

        <div className="flex justify-end gap-4 mt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate(`/admin/courses/${effectiveCourseSlug}/edit`)
              }
            >
              Back
            </Button>

            <Button type="submit" disabled={isAdding || isUpdating || form.exercises.length === 0}>
              {isAdding || isUpdating
                ? "Saving..."
                : isNew
                ? "Create Lesson"
                : "Update Lesson"}
            </Button>
          </div>
      </CardContent>
    </Card>
   </form>
  </div>
);
}

export default EditLessonPage;
